// SAD-491 (SHAPE-03): Pursuit stakeholder intelligence, the second section of
// the Customer Themes surface (see CaptureThemesPanel.tsx).
//
// NO ROSTER READ EXISTS (B3.2-06 backend gap, out of scope here): zou's
// browser-facing ProjectDataService has no ListPursuitStakeholders/
// GetPursuitStakeholder — only upsertPursuitStakeholder,
// recordStakeholderAssessment and listStakeholderAssessments (keyed by a
// stakeholder_id the caller already holds). So the roster below is session
// state, not a server-backed list: it only ever contains stakeholders added
// in this page session. The selected stakeholder's id is mirrored into the
// `stakeholder` search param so a reload — which loses the roster — can
// still resolve that one stakeholder's assessments via
// listStakeholderAssessments and so a link can deep-link to it directly.
//
// MASKING is the point of this section (B3.2-06.x): ListStakeholderAssessments
// blanks influence_band, relationship_posture, priorities, rationale and
// uncertainty_note on every row for a caller lacking view_sensitive — never
// dropped, never a count of how many were hidden. Requiring influence band
// and rationale on write (see RecordAssessmentForm) means a row this surface
// authors can never itself land with all five sensitive fields blank, so an
// all-blank row read back is unambiguously a masked one, never an honestly
// empty one — see isWithheld below.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Lock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorState } from '../Delivery/_shared/components/PageStates';
import { CONFIDENCE_LEVELS, formatTs, labelOf, PURSUIT_STAKEHOLDER_AFFILIATIONS, STAKEHOLDER_ASSESSMENT_AUTHORSHIPS, STAKEHOLDER_ASSESSMENT_STATES, STAKEHOLDER_INFLUENCE_BANDS, STAKEHOLDER_RELATIONSHIP_POSTURES, tsMs } from './constants';
import { useRecordStakeholderAssessment, useStakeholderAssessments, useUpsertPursuitStakeholder } from './themeHooks';
const STAKEHOLDER_PARAM = 'stakeholder';
export function CaptureStakeholdersSection({ captureProjectId, correlationId }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [roster, setRoster] = useState([]);
    const selectedId = searchParams.get(STAKEHOLDER_PARAM) ?? '';
    const selectStakeholder = (id)=>{
        const next = new URLSearchParams(searchParams);
        next.set(STAKEHOLDER_PARAM, id);
        setSearchParams(next, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.capture.stakeholdersSectionRoot,
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "text-sm font-medium text-muted-foreground",
                children: "Pursuit stakeholders"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Stakeholders added here stay listed for this browser session only; the open stakeholder stays addressable by this page's link even after a reload."
            }),
            /*#__PURE__*/ _jsx(AddStakeholderForm, {
                captureProjectId: captureProjectId,
                correlationId: correlationId,
                onCreated: (s)=>{
                    setRoster((prev)=>[
                            s,
                            ...prev.filter((r)=>r.id !== s.id)
                        ]);
                    selectStakeholder(s.id);
                }
            }),
            roster.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: roster.map((s)=>/*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            "data-testid": Testids.capture.stakeholderRosterRow,
                            "data-stakeholder-id": s.id,
                            onClick: ()=>selectStakeholder(s.id),
                            className: `w-full rounded border px-2 py-1 text-left text-xs ${s.id === selectedId ? 'border-blue-400 bg-blue-50' : ''}`,
                            children: [
                                s.unresolvedName || 'Unnamed stakeholder',
                                s.unresolvedOrganization ? ` · ${s.unresolvedOrganization}` : ''
                            ]
                        })
                    }, s.id))
            }),
            selectedId ? /*#__PURE__*/ _jsx(StakeholderDetail, {
                captureProjectId: captureProjectId,
                stakeholderId: selectedId,
                rosterEntry: roster.find((r)=>r.id === selectedId),
                correlationId: correlationId
            }) : /*#__PURE__*/ _jsx(EmptyState, {
                title: "No stakeholder selected",
                description: "Add a stakeholder above, or select one from this session's list, to view or record its assessment history."
            })
        ]
    });
}
function AddStakeholderForm({ captureProjectId, correlationId, onCreated }) {
    const upsert = useUpsertPursuitStakeholder(correlationId);
    const [name, setName] = useState('');
    const [organization, setOrganization] = useState('');
    const [affiliation, setAffiliation] = useState(PURSUIT_STAKEHOLDER_AFFILIATIONS[0].value);
    const [pursuitRole, setPursuitRole] = useState('');
    const submit = ()=>{
        const trimmed = name.trim();
        if (!trimmed) return;
        upsert.mutate({
            captureProjectId,
            unresolvedName: trimmed,
            unresolvedOrganization: organization,
            affiliation,
            pursuitRole
        }, {
            onSuccess: (resp)=>{
                if (resp.stakeholder) onCreated(resp.stakeholder);
                setName('');
                setOrganization('');
                setPursuitRole('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "py-3",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Add stakeholder"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "space-y-2",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Name"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: name,
                                    onChange: (e)=>setName(e.target.value),
                                    placeholder: "Unresolved name",
                                    "data-testid": Testids.capture.stakeholderAddNameInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Organization"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: organization,
                                    onChange: (e)=>setOrganization(e.target.value),
                                    placeholder: "Unresolved organization",
                                    "data-testid": Testids.capture.stakeholderAddOrganizationInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-40 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Affiliation"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: affiliation,
                                    onValueChange: setAffiliation,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.capture.stakeholderAddAffiliationSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: PURSUIT_STAKEHOLDER_AFFILIATIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Pursuit role"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: pursuitRole,
                                    onChange: (e)=>setPursuitRole(e.target.value),
                                    placeholder: "e.g. Contracting officer",
                                    "data-testid": Testids.capture.stakeholderAddPursuitRoleInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submit,
                            disabled: !name.trim() || upsert.isPending,
                            "data-testid": Testids.capture.stakeholderAddSubmitButton,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-4 w-4"
                            }),
                            children: "Add stakeholder"
                        })
                    ]
                })
            })
        ]
    });
}
function StakeholderDetail({ captureProjectId, stakeholderId, rosterEntry, correlationId }) {
    const assessments = useStakeholderAssessments(captureProjectId, stakeholderId);
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-stakeholder-id": stakeholderId,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: rosterEntry?.unresolvedName || `Stakeholder ${stakeholderId}`
                    }),
                    rosterEntry?.unresolvedOrganization && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: rosterEntry.unresolvedOrganization
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    assessments.isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-16"
                    }) : assessments.isError ? /*#__PURE__*/ _jsx(ErrorState, {
                        title: "Unable to load this stakeholder's assessments",
                        error: assessments.error
                    }) : (assessments.data ?? []).length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                        title: "No assessments recorded yet",
                        description: "Record the first assessment below — influence band and rationale are required."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: [
                            ...assessments.data ?? []
                        ].sort((a, b)=>tsMs(b.createdAt) - tsMs(a.createdAt)).map((a)=>/*#__PURE__*/ _jsx(AssessmentRow, {
                                assessment: a
                            }, a.id))
                    }),
                    /*#__PURE__*/ _jsx(RecordAssessmentForm, {
                        captureProjectId: captureProjectId,
                        stakeholderId: stakeholderId,
                        correlationId: correlationId
                    })
                ]
            })
        ]
    });
}
function AssessmentRow({ assessment }) {
    // All five sensitive fields blank is only ever reachable by the masking
    // path in ListStakeholderAssessments — RecordAssessmentForm below never
    // lets influenceBand or rationale leave this surface blank, so this
    // condition is unambiguous, never a false positive from an honest gap.
    const withheld = !assessment.influenceBand && !assessment.relationshipPosture && !assessment.priorities && !assessment.rationale && !assessment.uncertaintyNote;
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.capture.stakeholderAssessmentRow,
        "data-assessment-id": assessment.id,
        className: "space-y-2 rounded border p-2 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-muted-foreground",
                children: [
                    labelOf(CONFIDENCE_LEVELS, assessment.confidence),
                    " confidence ·",
                    ' ',
                    assessment.authorship === 'ai_suggested' ? 'AI-suggested' : 'human',
                    " ·",
                    ' ',
                    labelOf(STAKEHOLDER_ASSESSMENT_STATES, assessment.assessmentState),
                    assessment.sourceKind ? ` · ${assessment.sourceKind}` : '',
                    assessment.sourceRef ? ` (${assessment.sourceRef})` : '',
                    " · as of",
                    ' ',
                    formatTs(assessment.asOfDate),
                    " · created ",
                    formatTs(assessment.createdAt),
                    assessment.createdBy ? ` by ${assessment.createdBy}` : ''
                ]
            }),
            withheld ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.capture.stakeholderAssessmentWithheldBox,
                className: "flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900",
                children: [
                    /*#__PURE__*/ _jsx(Lock, {
                        className: "mt-0.5 h-3.5 w-3.5 shrink-0",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Pursuit intelligence withheld — this assessment exists, but you don't have permission to view its contents."
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Influence:"
                            }),
                            ' ',
                            labelOf(STAKEHOLDER_INFLUENCE_BANDS, assessment.influenceBand)
                        ]
                    }),
                    assessment.relationshipPosture && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Posture:"
                            }),
                            ' ',
                            labelOf(STAKEHOLDER_RELATIONSHIP_POSTURES, assessment.relationshipPosture)
                        ]
                    }),
                    assessment.priorities && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Priorities:"
                            }),
                            " ",
                            assessment.priorities
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Rationale:"
                            }),
                            " ",
                            assessment.rationale
                        ]
                    }),
                    assessment.uncertaintyNote && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Uncertainty:"
                            }),
                            " ",
                            assessment.uncertaintyNote
                        ]
                    })
                ]
            })
        ]
    });
}
function RecordAssessmentForm({ captureProjectId, stakeholderId, correlationId }) {
    const record = useRecordStakeholderAssessment(correlationId);
    const [influenceBand, setInfluenceBand] = useState(STAKEHOLDER_INFLUENCE_BANDS[0].value);
    const [relationshipPosture, setRelationshipPosture] = useState(STAKEHOLDER_RELATIONSHIP_POSTURES[0].value);
    const [priorities, setPriorities] = useState('');
    const [rationale, setRationale] = useState('');
    const [uncertaintyNote, setUncertaintyNote] = useState('');
    const [confidence, setConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const [sourceKind, setSourceKind] = useState('');
    const [sourceRef, setSourceRef] = useState('');
    const [authorship, setAuthorship] = useState(STAKEHOLDER_ASSESSMENT_AUTHORSHIPS[0].value);
    const [assessmentState, setAssessmentState] = useState(STAKEHOLDER_ASSESSMENT_STATES[0].value);
    const [asOfDate, setAsOfDate] = useState('');
    const trimmedRationale = rationale.trim();
    const guardMessage = !influenceBand ? 'Influence band is required before this assessment can be saved.' : !trimmedRationale ? 'Rationale is required before this assessment can be saved.' : '';
    const submit = ()=>{
        if (!influenceBand || !trimmedRationale) return;
        record.mutate({
            captureProjectId,
            stakeholderId,
            influenceBand,
            relationshipPosture,
            priorities,
            rationale: trimmedRationale,
            uncertaintyNote,
            confidence,
            sourceKind,
            sourceRef,
            authorship,
            assessmentState,
            asOfDate: asOfDate ? new Date(asOfDate) : null
        }, {
            onSuccess: ()=>{
                setPriorities('');
                setRationale('');
                setUncertaintyNote('');
                setSourceRef('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 border-t pt-3",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-medium",
                children: "Record assessment"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Influence band"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: influenceBand,
                                onValueChange: setInfluenceBand,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentInfluenceBandSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_INFLUENCE_BANDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Relationship posture"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: relationshipPosture,
                                onValueChange: setRelationshipPosture,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentRelationshipPostureSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_RELATIONSHIP_POSTURES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Confidence"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: confidence,
                                onValueChange: setConfidence,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentConfidenceSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Authorship"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: authorship,
                                onValueChange: setAuthorship,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentAuthorshipSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_ASSESSMENT_AUTHORSHIPS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "State"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: assessmentState,
                                onValueChange: setAssessmentState,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentStateSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_ASSESSMENT_STATES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Source kind"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: sourceKind,
                                onChange: (e)=>setSourceKind(e.target.value),
                                placeholder: "e.g. meeting",
                                "data-testid": Testids.capture.stakeholderAssessmentSourceKindInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[140px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Source reference"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: sourceRef,
                                onChange: (e)=>setSourceRef(e.target.value),
                                placeholder: "Link or citation",
                                "data-testid": Testids.capture.stakeholderAssessmentSourceRefInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "As of date"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                type: "date",
                                value: asOfDate,
                                onChange: (e)=>setAsOfDate(e.target.value),
                                "data-testid": Testids.capture.stakeholderAssessmentAsOfDateInput
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Priorities"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: priorities,
                        onChange: (e)=>setPriorities(e.target.value),
                        rows: 1,
                        "data-testid": Testids.capture.stakeholderAssessmentPrioritiesInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Rationale"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: rationale,
                        onChange: (e)=>setRationale(e.target.value),
                        rows: 2,
                        "data-testid": Testids.capture.stakeholderAssessmentRationaleInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Uncertainty note"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: uncertaintyNote,
                        onChange: (e)=>setUncertaintyNote(e.target.value),
                        rows: 1,
                        "data-testid": Testids.capture.stakeholderAssessmentUncertaintyNoteInput
                    })
                ]
            }),
            guardMessage && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-amber-700",
                children: guardMessage
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: submit,
                disabled: !influenceBand || !trimmedRationale || record.isPending,
                "data-testid": Testids.capture.stakeholderAssessmentSubmitButton,
                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                    className: "h-3.5 w-3.5"
                }),
                children: "Save assessment"
            })
        ]
    });
}
